import React from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Button, Paper, TableRow, Typography } from '@mui/material'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import {
  useCancelDistributionInstructionMutation,
  useGetAssetDistributionInstructionDetailsQuery
} from '../../../../api-services/asset-holdings/distributionInstructionApi'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import MUIDataTable, { type MUIDataTableOptions, type SelectableRows } from 'mui-datatables'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'
import { NumberFormatter } from '../../../../utility/formatters/NumberFormatter'
import AssetDistributionStatusChip from '../../../../views/components/chips/AssetDistributionStatusChip'
import {
  type AssetDistributionInstructionDetail,
  type AssetRatedUsageDetails
} from '../../../../api-schema-types/asset-holdings/DistributionInstructionDomainDtos'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { selectCancelInstructionButtons, setCancelButtonsEnabled } from './editDistributionInstructionsSlice'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import Refresh from '@mui/icons-material/Refresh'

const DistributionInstructionPage: React.FC = () => {
  const params = useParams<any>()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const goTo = (location: string): void => {
    history.push(location)
  }

  let distributionInstructionId: number | null = null
  // Initialise params
  if (Boolean(params.distributionInstructionId) && distributionInstructionId !== '') {
    distributionInstructionId = params.distributionInstructionId
  }

  const skipQuery = distributionInstructionId === null

  const getDistributionInstructionsApiResult = useGetAssetDistributionInstructionDetailsQuery({ assetDistributionInstructionId: distributionInstructionId }, { skip: skipQuery })
  const refetchGetDistributionInstructionsApiResult = getDistributionInstructionsApiResult.refetch
  const cancelButtonsEnabled: boolean = useAppSelector<boolean>(selectCancelInstructionButtons)
  const [apiCancelDistributionInstructionTrigger] = useCancelDistributionInstructionMutation()

  const isCancelInstructionEnabled = (instruction: AssetDistributionInstructionDetail): boolean => {
    return (instruction.status === 'AWAITING_PAYMENT')
  }

  const handleRefetch = async (): Promise<void> => {
    await refetchGetDistributionInstructionsApiResult()
  }
  const renderInstructionAction = (instructionDataIndex: number): React.ReactElement => {
    if (getDistributionInstructionsApiResult.isSuccess) {
      const instruction = getDistributionInstructionsApiResult.data.details.assetDistributionInstruction
      if (isCancelInstructionEnabled(instruction)) {
        return (
            <Button onClick={() => {
              handleCancelDistributionInstruction(instruction)
            }} type={'button'} variant={'outlined'} color={'error'} disabled={cancelButtonsEnabled}>
              <DeleteForeverIcon/>
              Cancel
            </Button>
        )
      }
    }
    return <></>
  }

  const handleCancelDistributionInstruction = (instruction: AssetDistributionInstructionDetail): void => {
    dispatch(setCancelButtonsEnabled(true))
    const choice = window.confirm(
      'Are you sure you want to Cancel?'
    )
    if (choice) {
      if (getDistributionInstructionsApiResult.isSuccess) {
        if (isCancelInstructionEnabled(instruction)) {
          apiCancelDistributionInstructionTrigger({ assetDistributionInstructionIds: [instruction.id] })
            .unwrap()
            .then(async () => {
              await handleRefetch()
              dispatch(setNotification({
                open: true,
                severity: 'success',
                message: 'Instruction has been transitioned to CANCELLED.'
              }))
              dispatch(setCancelButtonsEnabled(false))
            })
            .catch((err) => {
              dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
              dispatch(setCancelButtonsEnabled(false))
            })
        }
      }
    } else {
      dispatch(setCancelButtonsEnabled(false))
    }
  }

  const conversionsColumns = [
    {
      name: 'numberOfCells',
      label: 'Number Of Cells',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'srcCurrencyCode',
      label: 'Source Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={tableMeta.rowData[1].toString()}/>
          )
        }
      }
    },
    {
      name: 'srcCurrency',
      label: 'Source Currency',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'srcAmount',
      label: 'Source Amount',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'currencyCode',
      label: 'Currency Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={tableMeta.rowData[4].toString()}/>
          )
        }
      }
    },
    {
      name: 'currency',
      label: 'Currency',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'amount',
      label: 'Amount',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'exchangeRate',
      label: 'Exchange Rate',
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    }
  ]

  const instructionColumns = [
    {
      name: 'id',
      label: 'Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'projectId',
      label: 'Project Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'projectName',
      label: 'Project',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'currencyCode',
      label: 'Currency',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={value}/>
          )
        }
      }
    },
    {
      name: 'billingType',
      label: 'Billing Type',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'numberCellOwnerAgreements',
      label: '# COAs',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 0)}</span>)
        }
      }
    },
    {
      name: 'numberOfCells',
      label: '# Of Cells',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 0)}</span>)
        }
      }
    },
    {
      name: 'electricityConsumed',
      label: 'Electricity Consumed',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 2)}</span>)
        }
      }
    },
    {
      name: 'amountPerCell',
      label: 'Amount per Cell',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: '',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            renderInstructionAction(tableMeta.rowIndex)
          )
        }
      }
    }
  ]

  const assetHoldersColumns = [
    {
      name: 'id',
      label: 'Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'assetHolderId',
      label: 'Asset Holder Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'assetHolder',
      label: 'Asset Holder',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'cellOwnerAgreementId',
      label: 'COA Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'numberOfCells',
      label: '# of Cells',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'accumulationCurrencyInstructionId',
      label: 'Accum. Curr. Instruction Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'accumulationCurrencyId',
      label: 'Accum. Currency Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'accumulationCurrencyCode',
      label: 'Accum. Currency Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={value}/>
          )
        }
      }
    },
    {
      name: 'amount',
      label: 'Amount',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'exchangeRate',
      label: 'Exchange Rate',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'charityContributionInstructionId',
      label: 'Charity Instruction Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'percentageDistribution',
      label: 'Percentage',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'organisationId',
      label: 'Charity Org. Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'organisation',
      label: 'Charity',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'charityContributionAmount',
      label: 'Charity Contribution',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<AssetDistributionStatusChip itemStatus={value}/>)
        }
      }
    }
  ]

  const optionsAssetHolders: MUIDataTableOptions = {
    expandableRows: false,
    enableNestedDataAccess: '.',
    selectableRowsHideCheckboxes: false,
    selectableRows: 'none' as SelectableRows,
    expandableRowsOnClick: false
  }

  const optionsCurrencyConversionTotals: MUIDataTableOptions = {
    expandableRows: false,
    expandableRowsOnClick: false,
    selectableRowsHideCheckboxes: true,
    selectableRows: 'none' as SelectableRows,
    fixedHeader: true
  }

  const optionsDistributionInstruction: MUIDataTableOptions = {
    expandableRows: true,
    enableNestedDataAccess: '.',
    selectableRowsHideCheckboxes: true,
    selectableRows: 'none' as SelectableRows,
    expandableRowsOnClick: true,
    renderExpandableRow: (rowData: string[], rowMeta: any) => {
      const ratedUsages: AssetRatedUsageDetails[] = []
      if (getDistributionInstructionsApiResult.isSuccess) {
        ratedUsages.push(getDistributionInstructionsApiResult.data.details.assetRatedUsage)
        return (
          <React.Fragment>
            <tr>
              <td colSpan={10}>
                <TableContainer component={Paper}>
                  <Table style={{ minWidth: '650' }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Id</TableCell>
                        <TableCell align="right">Billing Run Id</TableCell>
                        <TableCell align="right">Billing Type</TableCell>
                        <TableCell align="right">Total Rated Amount</TableCell>
                        <TableCell align="right">Elec. Consumed</TableCell>
                        <TableCell align="right">Usage Total</TableCell>
                        <TableCell align="right">Total Admin Fee</TableCell>
                        <TableCell align="right">Fx Rate</TableCell>
                        <TableCell align="right">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ratedUsages.map((ratedUsage: AssetRatedUsageDetails) => (
                        <TableRow key={ratedUsage.id}>
                          <TableCell>{ratedUsage.id}</TableCell>
                          <TableCell>{ratedUsage.billingRunMonthlyId}</TableCell>
                          <TableCell align="right">{ratedUsage.billingType}</TableCell>
                          <TableCell align="right">{NumberFormatter.roundToString(ratedUsage.totalRatedAmount, 8)}</TableCell>
                          <TableCell align="right">{ratedUsage.electricityConsumed}</TableCell>
                          <TableCell align="right">{ratedUsage.usageTotal}</TableCell>
                          <TableCell align="right">{NumberFormatter.roundToString(ratedUsage.totalAdminFee, 8)}</TableCell>
                          <TableCell align="right">{ratedUsage.fxRate}</TableCell>
                          <TableCell align="right">{ratedUsage.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </td>
            </tr>
          </React.Fragment>
        )
      }
    }
  }

  const testing = (instruction: AssetDistributionInstructionDetail): AssetDistributionInstructionDetail[] => {
    const instructionArray: AssetDistributionInstructionDetail[] = []
    instructionArray.push(instruction)
    return instructionArray
  }

  return (
    <>
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ p: 1, flexGrow: 1 }}>
        <Typography variant={'h4'}>Asset Distribution Instruction {distributionInstructionId}</Typography>
        { getDistributionInstructionsApiResult.isSuccess && (
          <AssetDistributionStatusChip itemStatus={getDistributionInstructionsApiResult.data.details.assetDistributionInstruction.status}/>
        )}
      </Box>
      <Box sx={{ p: 1 }}>
        <Button variant={'outlined'} type="button" onClick={handleRefetch}>
          <Refresh/>
        </Button>
      </Box>
      <Box sx={{ p: 1 }}>
        { getDistributionInstructionsApiResult.isSuccess && (
            <Button variant={'text'} type="button" onClick={() => { goTo(`/app/distributions/run/${getDistributionInstructionsApiResult.data.details.assetRatedUsage.billingRunMonthlyId}`) }}>
              <ArrowBackIosIcon />
            </Button>
        )}
      </Box>
    </Box>
    {getDistributionInstructionsApiResult.isLoading && <LoadingComponent />}
    {(getDistributionInstructionsApiResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(getDistributionInstructionsApiResult.error)} />}
    { getDistributionInstructionsApiResult.isSuccess && (
      <Paper>
        <Box sx={{ p: 4, mt: 3 }}>
          <MUIDataTable
            title = {'Asset Distribution Instruction with Asset Rated Usage details'}
            data = { testing(getDistributionInstructionsApiResult.data.details.assetDistributionInstruction) }
            columns = { instructionColumns }
            options = { optionsDistributionInstruction }>
          </MUIDataTable>
        </Box>

        <Box sx={{ p: 4, mt: 3 }}>
           <MUIDataTable
            title = {'Currency Conversions'}
            data = { getDistributionInstructionsApiResult.data.details.conversions }
            columns = { conversionsColumns }
            options = { optionsCurrencyConversionTotals }>
           </MUIDataTable>
        </Box>
        <Box sx={{ p: 4, mt: 3 }}>
           <MUIDataTable
            title = {'Asset Holder Instructions'}
            data = { getDistributionInstructionsApiResult.data.assetHolderInstructions }
            columns = { assetHoldersColumns }
            options = { optionsAssetHolders }>
           </MUIDataTable>
        </Box>
      </Paper>
    )}
    </>
  )
}

export default DistributionInstructionPage
